import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    CreateCredentialRequest,
    CreateCredentialResponse,
    UpdateCredentialRequest,
} from '@xver/apis/v1/kufer/registry-credentials/model';
import { useTranslation } from 'react-i18next';

import { REGISTRY_CREDENTIALS_QUERY_KEY } from './useRegistryCredentialsQuery';

type ApiError = { message?: string };

export function useCredentialMutations() {
    const { t } = useTranslation('containerEngine');
    const queryClient = useQueryClient();

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: REGISTRY_CREDENTIALS_QUERY_KEY });

    const createMutation = useMutation<CreateCredentialResponse, ApiError, CreateCredentialRequest>(
        {
            mutationFn: (data) => Endpoints.v1.Kufer.RegistryCredentials.createCredential(data),
            onSuccess: async () => {
                notifications.show({
                    title: t('credentials.toast.createTitle', 'Credential created'),
                    message: t(
                        'credentials.toast.createMessage',
                        'The credential is now available for container deployments.'
                    ),
                    icon: <IconCheck size={18} color="#22c55e" />,
                });
                await invalidate();
            },
            onError: (err) => {
                notifications.show({
                    title: t('credentials.toast.errorTitle', 'Action failed'),
                    message:
                        err?.message ??
                        t('credentials.toast.errorMessage', 'Please try again later.'),
                    icon: <IconExclamationCircle size={18} color="#eb5757" />,
                });
            },
        }
    );

    const updateMutation = useMutation<
        void,
        ApiError,
        { id: string; data: UpdateCredentialRequest }
    >({
        mutationFn: ({ id, data }) =>
            Endpoints.v1.Kufer.RegistryCredentials.updateCredential(id, data),
        onSuccess: async () => {
            notifications.show({
                title: t('credentials.toast.updateTitle', 'Credential updated'),
                message: t(
                    'credentials.toast.updateMessage',
                    'Changes will apply to future deployments.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: (err) => {
            notifications.show({
                title: t('credentials.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('credentials.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const deleteMutation = useMutation<void, ApiError, string>({
        mutationFn: (id) => Endpoints.v1.Kufer.RegistryCredentials.deleteCredential(id),
        onSuccess: async () => {
            notifications.show({
                title: t('credentials.toast.deleteTitle', 'Credential deleted'),
                message: t(
                    'credentials.toast.deleteMessage',
                    'The credential was removed from your account.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: (err) => {
            notifications.show({
                title: t('credentials.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('credentials.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    return { createMutation, updateMutation, deleteMutation };
}
